import { useSourceLanguage } from "../i18n/LocalizedCopy";
import useNavigationTranslations from "../i18n/useNavigationTranslations";
import React, { useEffect, useId, useLayoutEffect, useState } from "react";

// Keep the first visit focused on the page; restore an explicit user preference.
export const QUICK_NAV_STORAGE_KEY = "eternua.quickNav.contextualExpanded.v2";

// Exact routes keep informational pages and payment flows outside this navigation.
export function isQuickNavigationPage(pathname) {
  const path = String(pathname || "/").replace(/\/+$/, "") || "/";
  return [
    "/market", "/market/acheter", "/market/vendre", "/mes-eternua/vendre/classique",
    "/market/donner", "/market/services", "/market/babysitting",
    "/aujourdhui", "/mes-eternua", "/missions", "/mon-parcours",
  ].includes(path);
}

function readInitialPreference() {
  if (typeof window === "undefined") return false;

  try {
    return window.localStorage.getItem(QUICK_NAV_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

export function useCollapsibleQuickNavigation({ available = true } = {}) {
  const contentId = useId();
  const [preferredExpanded, setExpanded] = useState(readInitialPreference);
  const expanded = available && preferredExpanded;

  useLayoutEffect(() => {
    const className = "eternua-quick-nav-expanded";
    const roots = [document.documentElement, document.body];
    roots.forEach((root) => root.classList.toggle(className, expanded));

    return () => {
      roots.forEach((root) => root.classList.remove(className));
    };
  }, [expanded]);

  useEffect(() => {
    try {
      window.localStorage.setItem(QUICK_NAV_STORAGE_KEY, String(preferredExpanded));
    } catch {
      // Cette préférence purement visuelle reste facultative.
    }
  }, [preferredExpanded]);

  const handleShortcutClick = (event) => {
    const clickedLink = event.target.closest?.("a");
    if (!clickedLink || !event.currentTarget.contains(clickedLink)) return;

    if (window.matchMedia?.("(max-width: 768px)")?.matches) {
      setExpanded(false);
    }
  };

  return {
    available,
    contentId,
    expanded,
    handleShortcutClick,
    toggleNavigation: () => setExpanded((current) => !current),
  };
}

export function QuickNavigationToggle({ contentId, expanded, onToggle }) {
  useSourceLanguage();
  const { translateLabel } = useNavigationTranslations();
  return (
    <button
      type="button"
      className={`et-global-quick-nav__toggle${expanded ? " is-expanded" : ""}`}
      aria-expanded={expanded}
      aria-controls={contentId}
      aria-label={translateLabel(`${expanded ? "Masquer" : "Afficher"} les raccourcis personnels`)}
      onClick={onToggle}
    >
      <span className="et-global-quick-nav__toggle-icon" aria-hidden="true">✦</span>
      <strong className="et-global-quick-nav__toggle-label">{translateLabel("Raccourcis")}</strong>
      <span className="et-global-quick-nav__chevron" aria-hidden="true">⌄</span>
    </button>
  );
}

export default function CollapsibleQuickNavigation({
  children,
  contentId,
  expanded,
  hidden = false,
  onShortcutClick,
}) {
  useSourceLanguage();
  const { translateLabel } = useNavigationTranslations();
  const inaccessible = !expanded || hidden;

  return (
    <section
      className={`et-global-quick-nav${expanded ? " et-global-quick-nav--expanded" : ""}`}
      aria-label={translateLabel("Raccourcis personnels")}
      aria-hidden={inaccessible || undefined}
      inert={inaccessible}
    >
      <div
        id={contentId}
        className="et-global-quick-nav__collapsible"
        data-testid="quick-nav-collapsible"
        aria-hidden={inaccessible}
        inert={inaccessible}
      >
        <div className="et-global-quick-nav__content" onClick={onShortcutClick}>
          {children}
        </div>
      </div>
    </section>
  );
}
